if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 1200px)');
        this.mediaList = this.querySelector('[id^="Slider-Gallery"]');
        this.viewMoreButton = this.querySelector('[data-gallery-view-more]');
        this.mobileCurrent = this.querySelector('.slider-counter--current');
        this.mobileTotal = this.querySelector('.slider-counter--total');
        this.mobileProgressBar = this.querySelector('.slider-progress__bar');
        this.mobileStaticZoomIcon = this.querySelector('.product__media-icon--pdp-overlay-zoom');
        this.mobileStaticPlayIcon = this.querySelector('.product__media-icon--pdp-overlay-play');
        this.boundRefreshPdpGalleryLayout = this.refreshPdpGalleryLayout.bind(this);

        this.initializePdpGallery();
        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      resolveMediaIdAliases(mediaId) {
        if (!mediaId) return [];
        const ids = new Set([mediaId]);
        if (mediaId.startsWith('quickadd-')) {
          ids.add(mediaId.slice('quickadd-'.length));
        } else {
          ids.add(`quickadd-${mediaId}`);
        }
        return [...ids];
      }

      querySlideByMediaId(mediaId) {
        if (!this.elements.viewer || !mediaId) return null;
        for (const id of this.resolveMediaIdAliases(mediaId)) {
          const el = this.elements.viewer.querySelector(`[data-media-id="${id}"]`);
          if (el) return el;
        }
        return null;
      }

      queryThumbnailByMediaId(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return null;
        for (const id of this.resolveMediaIdAliases(mediaId)) {
          const el = this.elements.thumbnails.querySelector(`[data-target="${id}"]`);
          if (el) return el;
        }
        return null;
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;

        if (this.elements.thumbnails) {
          const thumbnail = this.queryThumbnailByMediaId(currentElement.dataset.mediaId);
          this.setActiveThumbnail(thumbnail);
        }
        this.updateMobileProgress(currentElement);
        this.syncMobileStaticOverlay(currentElement);
        this.setActiveMedia(currentElement.dataset.mediaId, false, { skipScroll: true });
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const { skipScroll = false } = options;
        const activeMedia =
          this.querySlideByMediaId(mediaId) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.queryThumbnailByMediaId(mediaId);
            if (activeThumbnail?.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!skipScroll) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);
        this.refreshPdpGalleryLayout();
        this.updateMobileProgress(activeMedia);
        this.syncMobileStaticOverlay(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.queryThumbnailByMediaId(mediaId);
        this.setActiveThumbnail(activeThumbnail);
        const thumbnailPosition =
          activeThumbnail?.dataset?.mediaPosition ??
          String(
            Math.max(
              Array.from(this.mediaList?.querySelectorAll('.product__media-item:not(.inactive-variant)') || []).indexOf(
                activeMedia
              ),
              0
            ) + 1
          );
        this.announceLiveRegion(activeMedia, thumbnailPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails?.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails?.slider && this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initializePdpGallery() {
        this.refreshPdpGalleryLayout();
        window.addEventListener('resize', this.boundRefreshPdpGalleryLayout);

        this.querySelectorAll('.product__modal-opener--image').forEach((opener) => {
          opener.addEventListener('click', (event) => {
            if (event.target.closest('.product__media-toggle')) return;
            const toggle = opener.querySelector('.product__media-toggle');
            if (!toggle) return;
            event.preventDefault();
            toggle.click();
          });
        });

        if (this.viewMoreButton) {
          this.viewMoreButton.addEventListener('click', () => {
            const hiddenSlide = this.querySelector('.product__media-item--desktop-hidden:not(.inactive-variant)');
            hiddenSlide?.querySelector('.product__media-toggle')?.click();
          });
        }

        const initialActiveMedia =
          this.mediaList?.querySelector('.product__media-item.is-active:not(.inactive-variant)') ||
          this.mediaList?.querySelector('.product__media-item:not(.inactive-variant)');
        if (!this.mql.matches) {
          const firstVisibleImageMedia = Array.from(this.mediaList?.querySelectorAll('.product__media-item') || []).find(
            (slide) =>
              !slide.classList.contains('inactive-variant') &&
              !slide.querySelector('.product__modal-opener--video, .product__modal-opener--external_video, .deferred-media')
          );
          if (firstVisibleImageMedia?.dataset?.mediaId) {
            this.setActiveMedia(firstVisibleImageMedia.dataset.mediaId, false, { skipScroll: true });
          }
        }
        const currentActiveMedia =
          this.mediaList?.querySelector('.product__media-item.is-active:not(.inactive-variant)') || initialActiveMedia;
        this.syncMobileStaticOverlay(currentActiveMedia);
        this.bindMobileOverlayModalTriggers();
      }

      getActiveSlide() {
        return (
          this.mediaList?.querySelector('.product__media-item.is-active:not(.inactive-variant)') ||
          this.mediaList?.querySelector('.product__media-item:not(.inactive-variant)') ||
          null
        );
      }

      bindMobileOverlayModalTriggers() {
        if (this.mobileStaticZoomIcon) {
          this.mobileStaticZoomIcon.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.mobileStaticZoomIcon.hidden) return;
            const toggle = this.getActiveSlide()?.querySelector(
              '.product__modal-opener--image .product__media-toggle'
            );
            toggle?.click();
          });
        }
        if (this.mobileStaticPlayIcon) {
          this.mobileStaticPlayIcon.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.mobileStaticPlayIcon.hidden) return;
            const toggle = this.getActiveSlide()?.querySelector(
              '.product__modal-opener--video .product__media-toggle, .product__modal-opener--external_video .product__media-toggle'
            );
            toggle?.click();
          });
        }
      }

      refreshPdpGalleryLayout() {
        if (!this.mediaList) return;

        this.reorderVisibleMediaForDesktop();

        const visibleSlides = Array.from(this.mediaList.querySelectorAll('.product__media-item')).filter(
          (slide) => !slide.classList.contains('inactive-variant')
        );

        visibleSlides.forEach((slide, index) => {
          slide.classList.remove(
            'product__media-item--desktop-featured',
            'product__media-item--desktop-secondary-large',
            'product__media-item--desktop-hidden'
          );

          if (index === 0) slide.classList.add('product__media-item--desktop-featured');
          if (visibleSlides.length === 2 && index === 1) slide.classList.add('product__media-item--desktop-secondary-large');
          if (index > 4) slide.classList.add('product__media-item--desktop-hidden');
        });

        if (this.viewMoreButton) {
          this.viewMoreButton.hidden = visibleSlides.length <= 5;
        }

        if (this.mobileTotal) {
          this.mobileTotal.textContent = visibleSlides.length || 1;
        }
      }

      isVideoSlide(slide) {
        return !!slide.querySelector('.product__modal-opener--video, .product__modal-opener--external_video, .deferred-media');
      }

      reorderVisibleMediaForDesktop() {
        const visibleSlides = Array.from(this.mediaList.querySelectorAll('.product__media-item')).filter(
          (slide) => !slide.classList.contains('inactive-variant')
        );

        const videoSlides = visibleSlides.filter((slide) => this.isVideoSlide(slide));
        if (!videoSlides.length) return;

        const imageSlides = visibleSlides.filter((slide) => !this.isVideoSlide(slide));
        const videoInsertIndex = this.mql.matches
          ? imageSlides.length >= 5
            ? 4
            : imageSlides.length
          : imageSlides.length;
        const orderedVisibleSlides = [...imageSlides];
        orderedVisibleSlides.splice(videoInsertIndex, 0, ...videoSlides);

        orderedVisibleSlides.forEach((slide) => this.mediaList.appendChild(slide));

        if (!this.elements.thumbnails) return;
        const thumbnailList = this.elements.thumbnails.querySelector('ul');
        if (!thumbnailList) return;

        const orderedVisibleMediaIds = orderedVisibleSlides.map((slide) => slide.dataset.mediaId);
        orderedVisibleMediaIds.forEach((mid) => {
          const thumbnail = this.queryThumbnailByMediaId(mid);
          if (thumbnail) thumbnailList.appendChild(thumbnail);
        });
      }

      updateMobileProgress(activeMedia) {
        if (!this.mobileCurrent || !this.mobileProgressBar || !this.mediaList || !activeMedia) return;
        const visibleSlides = Array.from(this.mediaList.querySelectorAll('.product__media-item')).filter(
          (slide) => !slide.classList.contains('inactive-variant')
        );
        const currentIndex = Math.max(visibleSlides.indexOf(activeMedia), 0) + 1;
        const total = Math.max(visibleSlides.length, 1);
        this.mobileCurrent.textContent = currentIndex;
        this.mobileProgressBar.style.setProperty('--gallery-progress', `${(currentIndex / total) * 100}%`);
      }

      syncMobileStaticOverlay(activeMedia) {
        if (!this.mobileStaticZoomIcon && !this.mobileStaticPlayIcon) return;

        if (this.mql.matches) {
          if (this.mobileStaticZoomIcon) this.mobileStaticZoomIcon.hidden = true;
          if (this.mobileStaticPlayIcon) this.mobileStaticPlayIcon.hidden = true;
          return;
        }

        const isImageSlide = !!activeMedia?.querySelector('.product__modal-opener--image');
        const isVideoSlide = !!activeMedia?.querySelector('.product__modal-opener--video, .product__modal-opener--external_video');
        if (this.mobileStaticZoomIcon) this.mobileStaticZoomIcon.hidden = !isImageSlide;
        if (this.mobileStaticPlayIcon) this.mobileStaticPlayIcon.hidden = !isVideoSlide;
      }
    }
  );
}
